import { CmcWebApi } from '@cmctechnology/webinvest-api-client-web-trading';
import { LocationType } from '@cmctechnology/webinvest-store-frontend';
import { configureStore, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IThematicWatchlistDetail, IThematicWatchlist, IWatchlist, IWatchlistItemPortfolio, IWatchlistItems, CloudApi } from './types';

export const WATCHLISTS_STORE_NAME = `watchlists`;

export interface IWatchlistsStore {
  listsHash: number;
  lists?: IWatchlist[];
  items: Record<number, IWatchlistItems>;
  selectedWatchlist: Record<string, number | undefined>;
  thematicListsHash: number;
  thematicLists?: (IThematicWatchlist | CloudApi.ICloudThematicWatchlist)[];
  thematicItems: Record<number | string, IThematicWatchlistDetail>;
  optoWatchlistsHash: number;
  optoWatchlists?: IThematicWatchlist[];
  optoItems: Record<number, IThematicWatchlistDetail>;
}

export const defaultWatchlistsStore: IWatchlistsStore = {
  listsHash: 0,
  items: {},
  selectedWatchlist: {},
  thematicListsHash: 0,
  thematicItems: {},
  optoWatchlistsHash: 0,
  optoItems: {}
};

const mapThematicWatchlistResponse = (watchlist: CmcWebApi.Watchlists.ThematicWatchlistResponseModel1): IThematicWatchlist => ({
  id: watchlist.Id,
  name: watchlist.Name,
  description: watchlist.Description,
  descriptionCode: watchlist.DescriptionCode,
  descriptionUrl: watchlist.DescriptionUrl,
  imageBannerUrl: watchlist.ImageBannerUrl,
  imageUrl: watchlist.ImageUrl,
  priority: watchlist.Priority
});

const mapCloudThematicWatchlistResponse = (watchlist: CloudApi.SystemWatchlistDto): CloudApi.ICloudThematicWatchlist => ({
  description: watchlist.description,
  descriptionCode: watchlist.description,
  descriptionUrl: watchlist.articleUrl,
  id: watchlist.id,
  imageBannerUrl: watchlist.bannerUrl,
  imageUrl: watchlist.thumbnailUrl,
  name: watchlist.name,
  priority: watchlist.sortOrder
});

type ThematicWatchlistResponse = {
  id: number;
  response?: CmcWebApi.Watchlists.ThematicWatchlistDetailResponseModel1;
  hash: number;
};

export const createWatchlistsStore = (initialState: IWatchlistsStore) =>
  createSlice({
    name: WATCHLISTS_STORE_NAME,
    initialState,
    reducers: {
      storeReset: () => defaultWatchlistsStore,

      watchlistsFetched: (store: IWatchlistsStore, action: PayloadAction<{ response?: CmcWebApi.Watchlists.WatchListsModel1; listsHash: number }>) => {
        if (!action.payload.response?.WatchLists) return;

        store.listsHash = action.payload.listsHash;
        store.lists = action.payload.response.WatchLists.map((watchlist) => ({
          count: watchlist.StockCount,
          created: watchlist.DateCreated.valueOf(),
          id: watchlist.Id,
          name: watchlist.Name
        }));
      },

      watchlistCreated: (store: IWatchlistsStore, action: PayloadAction<{ id: number; name: string; count: number; created: number }>) => {
        store.lists?.push({
          count: action.payload.count,
          created: action.payload.created,
          id: action.payload.id,
          name: action.payload.name
        });
        store.lists?.sort((a, b) => a.name.localeCompare(b.name));
      },

      watchlistDeleted: (store: IWatchlistsStore, action: PayloadAction<number>) => {
        store.lists = store.lists?.filter((l) => l.id !== action.payload);
        delete store.items[action.payload];
      },

      watchlistRenamed: (store: IWatchlistsStore, action: PayloadAction<{ id: number; name: string }>) => {
        const list = store.lists?.find((l) => l.id === action.payload.id);
        if (list) {
          list.name = action.payload.name;
        }
      },

      watchlistItemsFetched: (
        store: IWatchlistsStore,
        action: PayloadAction<{ id: number; response?: CmcWebApi.Watchlists.WatchListModel2; hash: number }>
      ) => {
        if (!action.payload.response) return;

        const watchlist = store.items[action.payload.id] ?? { id: action.payload.id, hash: 0, items: [] };
        watchlist.hash = action.payload.hash;
        watchlist.items = /* istanbul ignore next */
        action.payload.response?.Stocks.map((stock) => ({
          id: stock.WatchListItemId,
          instrument: stock.Stock,
          portfolio: {
            purchaseDate: stock.PortfolioPurchaseDate?.valueOf(),
            purchasePrice: stock.PortfolioPurchasePrice,
            purchaseQuantity: stock.PortfolioPurchaseQuantity
          }
        }));
        store.items[action.payload.id] = watchlist;
      },

      watchlistItemDeleted: (store: IWatchlistsStore, action: PayloadAction<{ id: number; itemId: number }>) => {
        store.items[action.payload.id].items = store.items[action.payload.id].items.filter((i) => i.id !== action.payload.itemId);
      },

      watchlistItemForInstrumentDeleted: (store: IWatchlistsStore, action: PayloadAction<{ id: number; instrument: string }>) => {
        store.items[action.payload.id].items = store.items[action.payload.id].items.filter((i) => i.instrument !== action.payload.instrument);
      },

      watchlistItemSaved: (store: IWatchlistsStore, action: PayloadAction<{ id: number; itemId: number; item: IWatchlistItemPortfolio }>) => {
        const item = store.items[action.payload.id].items.find((i) => i.id === action.payload.itemId);
        if (!item?.portfolio) return;
        item.portfolio.purchaseDate = action.payload.item.purchaseDate;
        item.portfolio.purchasePrice = action.payload.item.purchasePrice;
        item.portfolio.purchaseQuantity = action.payload.item.purchaseQuantity;
      },

      watchlistSelected: (store: IWatchlistsStore, action: PayloadAction<{ location: LocationType; id: number }>) => {
        store.selectedWatchlist[action.payload.location] = action.payload.id;
      },

      thematicWatchlistsFetched: (
        store: IWatchlistsStore,
        action: PayloadAction<{ response?: CmcWebApi.Watchlists.ThematicWatchlistsResponseModel1; listsHash: number }>
      ) => {
        if (!action.payload.response?.Items) return;

        store.thematicListsHash = action.payload.listsHash;

        store.thematicLists = action.payload.response.Items.sort((a, b) => {
          const prioritySort = a.Priority - b.Priority;
          return prioritySort === 0 ? a.Name.localeCompare(b.Name) : prioritySort;
        }).map((val) => mapThematicWatchlistResponse(val));
      },

      cloudThematicWatchlistsFetched: (store: IWatchlistsStore, action: PayloadAction<CloudApi.SystemWatchlistDto[] | undefined>) => {
        if (!action.payload) return;
        store.thematicLists = action.payload
          ?.sort((a, b) => {
            const prioritySort = a.sortOrder - b.sortOrder;
            return prioritySort === 0 ? a.name.localeCompare(b.name) : prioritySort;
          })
          .map((w) => mapCloudThematicWatchlistResponse(w));
      },

      optoWatchlistsFetched: (
        store: IWatchlistsStore,
        action: PayloadAction<{ response?: CmcWebApi.Watchlists.ThematicWatchlistsResponseModel1; listsHash: number }>
      ) => {
        if (!action.payload.response?.Items) return;

        store.optoWatchlistsHash = action.payload.listsHash;

        store.optoWatchlists = action.payload.response.Items.sort((a, b) => {
          const prioritySort = a.Priority - b.Priority;
          return prioritySort === 0 ? a.Name.localeCompare(b.Name) : prioritySort;
        }).map((val) => mapThematicWatchlistResponse(val));
      },

      thematicWatchlistFetched: (store: IWatchlistsStore, action: PayloadAction<ThematicWatchlistResponse>) => {
        if (!action.payload.response) return;

        const thematicWatchlist = store.thematicItems[action.payload.id] ?? {
          hash: 0,
          name: '',
          instruments: []
        };

        thematicWatchlist.hash = action.payload.hash;
        thematicWatchlist.name = action.payload.response.Name;
        thematicWatchlist.instruments = action.payload.response.Instruments;

        store.thematicItems[action.payload.id] = thematicWatchlist;
      },
      cloudThematicWatchlistFetched: (store: IWatchlistsStore, action: PayloadAction<CloudApi.SystemWatchlistFullDetailsDto | undefined>) => {
        const watchlist = action.payload;
        if (!watchlist) return;

        const thematicWatchlist = store.thematicItems[watchlist.id] ?? {
          hash: 0,
          name: '',
          instruments: []
        };

        thematicWatchlist.name = watchlist.name;
        thematicWatchlist.instruments = watchlist.items?.map((item) => item.instrument) ?? [];
        thematicWatchlist.items = watchlist.items ?? [];
        store.thematicItems[watchlist.id] = thematicWatchlist;
      },
      optoWatchlistFetched: (
        store: IWatchlistsStore,
        action: PayloadAction<{
          id: number;
          response?: CmcWebApi.Watchlists.ThematicWatchlistDetailResponseModel1;
          hash: number;
        }>
      ) => {
        if (!action.payload.response) return;

        const optoWatchlist = store.optoItems[action.payload.id] ?? {
          hash: 0,
          name: '',
          instruments: []
        };

        optoWatchlist.hash = action.payload.hash;
        optoWatchlist.name = action.payload.response.Name;
        optoWatchlist.instruments = action.payload.response.Instruments;

        store.optoItems[action.payload.id] = optoWatchlist;
      }
    }
  });

export const { actions, reducer } = createWatchlistsStore(defaultWatchlistsStore);

export const createStore = (initialStore: IWatchlistsStore = defaultWatchlistsStore) =>
  configureStore({ reducer: { [WATCHLISTS_STORE_NAME]: createWatchlistsStore(initialStore).reducer } });

export const WatchlistsStore = createStore();
